// apps/web/src/wizard/state.ts — Phase 4 plan 04-03.
//
// Lightweight React state container for the guest reservation
// wizard. The plan calls for `zustand`; the threat-model section
// explicitly forbids new npm packages ("The 04-03 task does NOT
// install any new npm packages"). The state shape is small
// enough to live in a `useSyncExternalStore` + module-scope
// object — we expose a `useWizard` hook that re-renders only
// when the slice it reads changes.
//
// Persistence (D-25 "wizard must survive reload on A4"):
//   - `serviceDate` + `partySize` ARE persisted to localStorage
//     (the inputs the user already typed).
//   - `selectedSlot` + `guest` are NOT persisted (a stale
//     selectedSlot would double-submit a now-stale row on A6).
//   - `consentText` + `consentAt` are NOT persisted (the
//     consent is a per-flow user action, not a preference).

import { useSyncExternalStore } from "react";

// ===== Types =====

export interface SelectedSlot {
  slotDefinitionId: string;
  startsAt: string;
  endsAt: string;
  durationMinutes: number;
  startTime: string;
  remainingSeats: number;
}

export interface GuestState {
  name: string;
  email: string;
  phone: string;
  consentAt: string | null;
  consentText: string | null;
  accepted: boolean;
}

export interface WizardState {
  serviceDate: string | null;
  partySize: number;
  selectedSlot: SelectedSlot | null;
  guest: GuestState;
}

const EMPTY_GUEST: GuestState = {
  name: "",
  email: "",
  phone: "",
  consentAt: null,
  consentText: null,
  accepted: false,
};

const INITIAL_STATE: WizardState = {
  serviceDate: null,
  partySize: 2,
  selectedSlot: null,
  guest: EMPTY_GUEST,
};

const STORAGE_KEY = "gastio.wizard";

// ===== Store =====

let state: WizardState = loadInitial();

const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function loadInitial(): WizardState {
  if (typeof window === "undefined") return INITIAL_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL_STATE;
    const parsed = JSON.parse(raw) as Partial<WizardState>;
    return {
      ...INITIAL_STATE,
      serviceDate:
        typeof parsed.serviceDate === "string" ? parsed.serviceDate : null,
      partySize:
        typeof parsed.partySize === "number" && parsed.partySize > 0
          ? parsed.partySize
          : 2,
    };
  } catch {
    return INITIAL_STATE;
  }
}

function persist() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        serviceDate: state.serviceDate,
        partySize: state.partySize,
      }),
    );
  } catch {
    // localStorage may be unavailable (private mode); in-memory
    // state still works.
  }
}

export function getState(): WizardState {
  return state;
}

export function setServiceDate(value: string | null): void {
  state = { ...state, serviceDate: value };
  emit();
  persist();
}

export function setPartySize(value: number): void {
  state = { ...state, partySize: value };
  emit();
  persist();
}

export function setSelectedSlot(value: SelectedSlot | null): void {
  state = { ...state, selectedSlot: value };
  emit();
}

export function setGuest(patch: Partial<GuestState>): void {
  state = {
    ...state,
    guest: { ...state.guest, ...patch },
  };
  emit();
}

export function captureConsent(args: {
  consentText: string;
  consentAt: string;
}): void {
  state = {
    ...state,
    guest: {
      ...state.guest,
      consentText: args.consentText,
      consentAt: args.consentAt,
      accepted: true,
    },
  };
  emit();
}

export function resetWizard(): void {
  state = { ...INITIAL_STATE };
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Read the entire state. Components that need a single slice
 * should call `useWizardSlice` instead.
 */
export function useWizard(): WizardState {
  return useSyncExternalStore(subscribe, getState, getState);
}

/**
 * Subscribe to a single slice; re-renders only when the
 * comparison function returns false.
 */
export function useWizardSlice<T>(
  pick: (s: WizardState) => T,
  isEqual: (a: T, b: T) => boolean = Object.is,
): T {
  return useSyncExternalStore(
    subscribe,
    () => {
      const next = pick(getState());
      // The React 18 `useSyncExternalStore` contract is to return
      // the same reference for unchanged snapshots; we compare
      // the picked slice to the previous one via the optional
      // `isEqual` callback and cache.
      const cached = sliceCache.get(pick) as { prev: T; value: T } | undefined;
      if (cached && isEqual(cached.prev, next)) {
        return cached.value;
      }
      const value = next;
      sliceCache.set(pick, { prev: next, value });
      return value;
    },
    () => getState() as unknown as T,
  );
}

// Tiny per-pick cache; pick functions are stable in practice
// (they're defined inline in the component), so we key by the
// function reference. If the same component re-renders with a
// different inline pick, the cache is harmless (it stores a
// stale entry that will be overwritten on the next render).
const sliceCache = new WeakMap<
  (s: WizardState) => unknown,
  { prev: unknown; value: unknown }
>();
